<i18n>
{
  "de": {
    "pageTitle": "Produkte",
    "allProducts": "Alle Produkte"
  },
  "en": {
    "pageTitle": "Products",
    "allProducts": "All Products"
  }
}
</i18n>

<template>
  <div>
    <section class="section">
      <div class="container">
        <div class="columns">
          <!-- Sidenav (hidden on mobile) -->
          <div
            class="column"
            :class="isMobileView ? 'is-hidden' : 'is-one-quarter'"
          >
            <sidenav />
          </div>

          <!-- Shop Content (filtered by category) -->
          <div class="column">
            <!-- Active Filter Indicator for Mobile -->
            <active-filter-indicator @visibility-changed="onFilterIndicatorVisibilityChanged" />

            <!-- Category header -->
            <div
              v-if="activeCategory"
              class="category-header on-body-background"
              :class="isMobileView ? '' : 'is-hidden'"
            >
              <nuxt-link
                :to="localePath({ name: 'id-shop-products', params: { id: artist.urlAlias } })"
                class="category-header__back"
              >
                ← {{ $t('allProducts') }}
              </nuxt-link>
              <h2 class="category-header__title">
                {{ activeCategory.name }}
              </h2>
            </div>

            <shop-content :category-filter="categorySlug" />
          </div>
        </div>
      </div>

      <!-- Desktop Shop Filters (rendered in sidenav via portal) -->
      <portal to="desktopFilterShop">
        <filter-shop />
      </portal>
    </section>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import sidenav from '~/components/sidenav'
import ShopContent from '~/components/shop/index.vue'
import FilterShop from '~/components/filter-shop'
import ActiveFilterIndicator from '~/components/active-filter-indicator'

export default {
  name: 'ShopProductsCategoryPage',

  components: {
    sidenav,
    ShopContent,
    FilterShop,
    ActiveFilterIndicator
  },

  transition: {
    name: 'shop-page',
    mode: 'out-in'
  },

  data() {
    return {
      filterIndicatorVisible: false,
      activeCategory: null
    }
  },

  head() {
    const categoryName = this.activeCategory?.name || this.categorySlug
    return {
      title: `${this.artist.nickname} - ${categoryName}`,
      meta: [
        { hid: 'robots', name: 'robots', content: 'noindex, follow' }
      ]
    }
  },

  computed: {
    ...mapState(['artist']),

    categorySlug() {
      return this.$route.params.category
    }
  },

  mounted() {
    // Set filter type to 'product' and sync category with Vuex
    this.setShopFilter({ type: 'product', category: this.categorySlug })
    this.loadCategory(this.categorySlug)
  },

  beforeDestroy() {
    // Clear category filter when leaving
    this.setShopFilter({ category: null })
  },

  methods: {
    ...mapActions(['setShopFilter']),

    loadCategory(slug) {
      // Find the category from artist's products
      if (this.artist?.products) {
        for (const product of this.artist.products) {
          if (product.categories) {
            const cat = product.categories.find(c => c.slug === slug)
            if (cat) {
              this.activeCategory = cat
              return
            }
          }
        }
      }
    },

    onFilterIndicatorVisibilityChanged(isVisible) {
      this.filterIndicatorVisible = isVisible
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.loadCategory(to.params.category)
      vm.setShopFilter({ type: 'product', category: to.params.category })
    })
  },

  async beforeRouteUpdate(to, from, next) {
    this.loadCategory(to.params.category)
    // Sync category with Vuex on route change
    this.setShopFilter({ category: to.params.category })
    next()
  }
}
</script>

<style lang="scss" scoped>
.category-header {
  margin-bottom: 20px;

  &__back {
    display: inline-block;
    margin-bottom: 8px;
    font-size: 0.9rem;
    text-decoration: none;
    opacity: 0.8;
    padding: 2px;

    &:hover {
      opacity: 1;
    }
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
  }
}
</style>
